<template>
  <div :class="{ 'modal-open': hasModalOpen }">
    <div class="page-header">
      <div class="header-content">
        <p class="header-eyebrow">After-Sales Service</p>
        <h1 class="page-title">售后服务管理</h1>
      </div>
    </div>
    
    <div class="action-buttons">
      <button 
        class="btn" 
        :class="{ 'active': activeTab === 'requests' }"
        @click="switchTab('requests')">
        <span class="btn-icon">📋</span>
        售后申请
        <span v-if="pendingCount > 0" class="badge">{{ pendingCount }}</span>
      </button>
      <button 
        class="btn" 
        :class="{ 'active': activeTab === 'records' }"
        @click="switchTab('records')">
        <span class="btn-icon">📝</span>
        售后记录
      </button>
    </div>
    
    <div v-if="loading" class="loading">加载中...</div>
    
    <div v-if="error" class="alert alert-danger">
      {{ error }}
    </div>
    
    <div v-if="!loading && filteredRequests.length > 0" class="after-sales-list">
      <div class="summary-bar">
        <div class="summary-item">
          <span class="summary-icon">⏳</span>
          <span class="summary-label">待处理</span>
          <span class="summary-value pending">{{ pendingCount }}</span>
        </div>
        <div class="summary-item">
          <span class="summary-icon">🔄</span>
          <span class="summary-label">处理中</span>
          <span class="summary-value processing">{{ processingCount }}</span>
        </div>
        <div class="summary-item">
          <span class="summary-icon">✅</span>
          <span class="summary-label">已完成</span>
          <span class="summary-value completed">{{ completedCount }}</span>
        </div>
        <div class="summary-item">
          <span class="summary-icon">❌</span>
          <span class="summary-label">已拒绝</span>
          <span class="summary-value rejected">{{ rejectedCount }}</span>
        </div>
      </div>
      
      <div class="request-list">
        <div v-for="request in filteredRequests" :key="request.id" class="card request-card">
          <!-- 状态进度条 -->
          <div class="status-timeline">
            <div 
              v-for="(step, index) in getStatusSteps(request.type)" 
              :key="index"
              class="timeline-step"
              :class="{
                'completed': step.status === 'completed',
                'current': step.status === 'current',
                'pending': step.status === 'pending'
              }"
            >
              <div class="step-icon">{{ step.icon }}</div>
              <div class="step-label">{{ step.label }}</div>
              <div v-if="index < getStatusSteps(request.type).length - 1" class="step-line"></div>
            </div>
          </div>
          
          <div class="request-header">
            <h3 class="request-id">售后申请 #{{ request.afterSalesId }}</h3>
            <span :class="['status-badge', getStatusClass(request.status)]">
              {{ getStatusLabel(request.status) }}
            </span>
          </div>
          
          <div class="request-info">
            <div class="info-row">
              <span class="info-icon">📦</span>
              <span class="info-label">订单号</span>
              <span class="info-value">{{ request.orderNumber }}</span>
            </div>
            <div class="info-row">
              <span class="info-icon">🛍️</span>
              <span class="info-label">商品</span>
              <span class="info-value">{{ request.productName || '未知商品' }}</span>
            </div>
            <div class="info-row">
              <span class="info-icon">👤</span>
              <span class="info-label">客户</span>
              <span class="info-value">{{ request.customerName }}</span>
            </div>
            <div class="info-row">
              <span class="info-icon">📋</span>
              <span class="info-label">类型</span>
              <span :class="['type-tag', getTypeClass(request.type)]">{{ request.type }}</span>
            </div>
            <div class="info-row">
              <span class="info-icon">💬</span>
              <span class="info-label">原因</span>
              <span class="info-value">{{ request.reason }}</span>
            </div>
            <div class="info-row">
              <span class="info-icon">🕐</span>
              <span class="info-label">申请时间</span>
              <span class="info-value">{{ request.createdAt }}</span>
            </div>
          </div>
          
          <div class="actions">
            <button 
              v-if="activeTab === 'requests' && request.status !== '已完成'"
              class="btn btn-info" 
              @click="viewDetails(request)"
              :disabled="!request">
              查看具体信息
            </button>
            <button 
              v-if="activeTab === 'requests' && (request.status === '待处理' || request.status === '待退款')"
              class="btn btn-success" 
              @click="openRefundForm(request)"
              :disabled="!request">
              同意退款
            </button>
            <button 
              v-if="activeTab === 'requests' && request.status === '待处理'"
              class="btn btn-warning" 
              @click="openReturnForm(request)"
              :disabled="!request">
              同意退货
            </button>
            <button 
              v-if="activeTab === 'requests' && request.status === '待退货'"
              class="btn btn-success" 
              @click="confirmReturnReceipt(request)"
              :disabled="!request">
              确认收货
            </button>
            <button 
              v-if="activeTab === 'requests' && request.status === '待处理'"
              class="btn btn-danger" 
              @click="rejectAfterSales(request)"
              :disabled="!request">
              拒绝售后
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <div v-if="!loading && filteredRequests.length === 0" class="alert alert-danger">
      {{ activeTab === 'requests' ? '当前没有售后申请。' : '当前没有售后记录。' }}
    </div>
    
    <!-- 详情弹窗 -->
    <div v-if="showDetailModal" class="notification-modal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>售后申请详情</h3>
          <button class="close-btn" @click="closeDetailModal">×</button>
        </div>
        <div class="modal-body">
          <p><strong>售后单号:</strong> {{ selectedRequest.afterSalesId }}</p>
          <p><strong>订单号:</strong> {{ selectedRequest.orderNumber }}</p>
          <p><strong>商品信息:</strong> {{ selectedRequest.productName || '未知商品' }}</p>
          <p><strong>客户名称:</strong> {{ selectedRequest.customerName }}</p>
          <p><strong>用户诉求:</strong> {{ selectedRequest.reason }}</p>
          <p><strong>申请类型:</strong> {{ selectedRequest.type }}</p>
          <p><strong>申请时间:</strong> {{ selectedRequest.createdAt }}</p>
          <p><strong>当前状态:</strong> {{ selectedRequest.status }}</p>
          <p v-if="selectedRequest.returnAddress"><strong>退货地址:</strong> {{ selectedRequest.returnAddress }}</p>
          <p v-if="selectedRequest.returnContact"><strong>联系人:</strong> {{ selectedRequest.returnContact }}</p>
          <p v-if="selectedRequest.returnPhone"><strong>联系电话:</strong> {{ selectedRequest.returnPhone }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn" @click="closeDetailModal">关闭</button>
        </div>
      </div>
    </div>
    
    <!-- 确认弹窗 -->
    <div v-if="showConfirmModal" class="notification-modal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ confirmModalTitle }}</h3>
          <button class="close-btn" @click="closeConfirmModal">×</button>
        </div>
        <div class="modal-body">
          <p>{{ confirmModalMessage }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn" @click="closeConfirmModal">取消</button>
          <button class="btn btn-primary" @click="confirmAction">确认</button>
        </div>
      </div>
    </div>
    
    <!-- 退款表单弹窗 -->
    <div v-if="showRefundForm" class="notification-modal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>退款处理</h3>
          <button class="close-btn" @click="closeRefundForm">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="refundAmount">退款金额：</label>
            <input 
              type="number" 
              id="refundAmount" 
              v-model="refundForm.refundAmount" 
              placeholder="请输入退款金额"
              min="0"
              step="0.01"
            />
            <small class="text-muted">退款金额不能超过商品实付金额</small>
          </div>
          <div class="form-group">
            <label for="refundReason">退款原因：</label>
            <textarea 
              id="refundReason" 
              v-model="refundForm.refundReason" 
              placeholder="请输入退款原因"
              rows="3"
            ></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn" @click="closeRefundForm">取消</button>
          <button class="btn btn-primary" @click="submitRefund">确认退款</button>
        </div>
      </div>
    </div>
    
    <!-- 退货地址表单弹窗 -->
    <div v-if="showReturnForm" class="notification-modal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>退货地址设置</h3>
          <button class="close-btn" @click="closeReturnForm">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="returnAddress">退货地址：</label>
            <textarea 
              id="returnAddress" 
              v-model="returnForm.address" 
              placeholder="请输入退货地址"
              rows="2"
            ></textarea>
          </div>
          <div class="form-group">
            <label for="returnContact">联系人：</label>
            <input 
              type="text" 
              id="returnContact" 
              v-model="returnForm.contact" 
              placeholder="请输入联系人姓名"
            />
          </div>
          <div class="form-group">
            <label for="returnPhone">联系电话：</label>
            <input 
              type="text" 
              id="returnPhone" 
              v-model="returnForm.phone" 
              placeholder="请输入联系电话"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn" @click="closeReturnForm">取消</button>
          <button class="btn btn-primary" @click="submitReturnAddress">确认并发送退货地址</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SellerAfterSales',
  data() {
    return {
      afterSalesRequests: [],
      loading: true,
      error: '',
      activeTab: 'requests',
      showDetailModal: false,
      selectedRequest: {},
      showConfirmModal: false,
      confirmModalTitle: '',
      confirmModalMessage: '',
      currentRequest: null,
      currentAction: '',
      showRefundForm: false,
      refundForm: {
        refundAmount: '',
        refundReason: ''
      },
      showReturnForm: false,
      returnForm: {
        address: '',
        contact: '',
        phone: ''
      }
    }
  },
  computed: {
    hasModalOpen() {
      return this.showDetailModal || this.showConfirmModal || this.showRefundForm || this.showReturnForm
    },
    filteredRequests() {
      if (this.activeTab === 'requests') {
        return this.afterSalesRequests.filter(req => req.status !== '已完成' && req.status !== '已拒绝')
      } else {
        return this.afterSalesRequests.filter(req => req.status === '已完成' || req.status === '已拒绝')
      }
    },
    pendingCount() {
      return this.afterSalesRequests.filter(req => req.status === '待处理').length
    },
    processingCount() {
      return this.afterSalesRequests.filter(req => req.status === '处理中' || req.status === '待退货' || req.status === '待退款').length
    },
    completedCount() {
      return this.afterSalesRequests.filter(req => req.status === '已完成').length
    },
    rejectedCount() {
      return this.afterSalesRequests.filter(req => req.status === '已拒绝').length
    }
  },
  created() {
    this.fetchAfterSalesRequests()
  },
  methods: {
    async fetchAfterSalesRequests() {
      try {
        this.loading = true
        const sellerId = localStorage.getItem('sellerId')
        const shopId = localStorage.getItem('shopId')
        
        if (!sellerId) {
          this.error = '请先登录'
          return
        }
        
        // 优先使用shopId查询，如果没有则使用sellerId
        let response
        if (shopId) {
          response = await this.$axios.get(`/after-sales/shop/${shopId}`)
        } else {
          response = await this.$axios.get(`/after-sales/seller/${sellerId}`)
        }
        const data = response.data
        // 转换数据结构以适配前端显示
        this.afterSalesRequests = data.map(item => ({
          id: item.id,
          afterSalesId: item.afterSalesId,
          orderNumber: item.buyerId, // 显示订单ID（数据库表中的id）
          productName: item.productName,
          customerName: item.customerName,
          type: item.type,
          reason: item.reason,
          createdAt: item.createdAt,
          status: item.status,
          shopId: item.shopId
        }))
        this.error = ''
      } catch (err) {
        this.error = '获取售后申请失败'
        console.error(err)
        // 失败时使用模拟数据
        this.afterSalesRequests = this.getMockData()
      } finally {
        this.loading = false
      }
    },

    // 模拟售后申请数据
    getMockData() {
      return [
        {
          afterSalesId: 'SH202603100001',
          orderNumber: 'ORD2026031000001',
          sku: 'SKU0001',
          id: 1,
          productName: '商品A',
          customerName: '客户1',
          type: '退款',
          reason: '商品质量问题',
          createdAt: '2024-01-01 10:00:00',
          status: '待处理'
        },
        {
          afterSalesId: 'SH202603100002',
          orderNumber: 'ORD2026031000002',
          sku: 'SKU0002',
          id: 2,
          productName: '商品B',
          customerName: '客户2',
          type: '换货',
          reason: '商品尺码不合适',
          createdAt: '2024-01-02 14:30:00',
          status: '处理中'
        },
        {
          afterSalesId: 'SH202603100003',
          orderNumber: 'ORD2026031000003',
          sku: 'SKU0003',
          id: 3,
          productName: '商品C',
          customerName: '客户3',
          type: '退款',
          reason: '重复购买',
          createdAt: '2024-01-03 09:15:00',
          status: '已完成'
        }
      ]
    },

    // 切换标签
    switchTab(tab) {
      this.activeTab = tab
    },
    
    // 获取售后流程步骤
    getStatusSteps(type) {
      const currentStatus = this.currentRequest?.status || ''
      if (type === '退款') {
        return [
          { icon: '📝', label: '申请提交', status: currentStatus === '待处理' ? 'current' : (currentStatus !== '' ? 'completed' : 'pending') },
          { icon: '⚖️', label: '审核处理', status: currentStatus === '处理中' ? 'current' : (currentStatus === '已完成' || currentStatus === '待退款' ? 'completed' : 'pending') },
          { icon: '💰', label: '退款完成', status: currentStatus === '已完成' ? 'completed' : 'pending' }
        ]
      } else if (type === '换货') {
        return [
          { icon: '📝', label: '申请提交', status: currentStatus === '待处理' ? 'current' : (currentStatus !== '' ? 'completed' : 'pending') },
          { icon: '📦', label: '等待退货', status: currentStatus === '待退货' ? 'current' : (currentStatus === '已完成' || currentStatus === '处理中' ? 'completed' : 'pending') },
          { icon: '🔄', label: '换货处理', status: currentStatus === '处理中' ? 'current' : (currentStatus === '已完成' ? 'completed' : 'pending') },
          { icon: '✅', label: '完成', status: currentStatus === '已完成' ? 'completed' : 'pending' }
        ]
      } else {
        return [
          { icon: '📝', label: '申请提交', status: 'completed' },
          { icon: '⚖️', label: '处理中', status: 'current' },
          { icon: '✅', label: '完成', status: 'pending' }
        ]
      }
    },
    
    // 获取状态样式类
    getStatusClass(status) {
      switch (status) {
        case '待处理': return 'status-pending'
        case '处理中': return 'status-processing'
        case '待退货': return 'status-waiting-return'
        case '待退款': return 'status-waiting-refund'
        case '已完成': return 'status-completed'
        case '已拒绝': return 'status-rejected'
        default: return 'status-unknown'
      }
    },
    
    // 获取状态标签
    getStatusLabel(status) {
      switch (status) {
        case '待处理': return '⏳ 待处理'
        case '处理中': return '🔄 处理中'
        case '待退货': return '📦 待退货'
        case '待退款': return '💰 待退款'
        case '已完成': return '✅ 已完成'
        case '已拒绝': return '❌ 已拒绝'
        default: return '❓ 未知'
      }
    },
    
    // 获取类型样式类
    getTypeClass(type) {
      switch (type) {
        case '退款': return 'type-refund'
        case '换货': return 'type-exchange'
        case '退货': return 'type-return'
        default: return 'type-other'
      }
    },

    // 查看具体信息
    viewDetails(request) {
      this.selectedRequest = request
      this.showDetailModal = true
    },
    
    // 显示退款表单
    openRefundForm(request) {
      this.currentRequest = request
      this.refundForm.refundAmount = ''
      this.refundForm.refundReason = ''
      this.showRefundForm = true
    },
    
    // 关闭退款表单
    closeRefundForm() {
      this.showRefundForm = false
      this.currentRequest = null
      this.refundForm.refundAmount = ''
      this.refundForm.refundReason = ''
    },
    
    // 提交退款
    async submitRefund() {
      if (!this.refundForm.refundAmount || !this.refundForm.refundReason) {
        alert('请填写完整的退款信息')
        return
      }
      
      const refundAmount = parseFloat(this.refundForm.refundAmount)
      if (refundAmount <= 0) {
        alert('退款金额必须大于0')
        return
      }
      
      try {
        // 调用后端API更新售后状态
        const response = await fetch(`/api/after-sales/${this.currentRequest.id}/status?status=已完成`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            refundAmount: refundAmount,
            refundReason: this.refundForm.refundReason
          })
        })
        
        if (response.ok) {
          // 更新本地状态
          this.currentRequest.status = '已完成'
          this.closeRefundForm()
          alert('退款处理成功！')
        } else {
          throw new Error('退款处理失败')
        }
      } catch (err) {
        console.error('退款处理失败', err)
        alert('退款处理失败，请稍后重试')
      }
    },
    
    // 关闭详情弹窗
    closeDetailModal() {
      this.showDetailModal = false
      this.selectedRequest = {}
    },

    // 确认售后
    confirmAfterSales(request) {
      this.currentRequest = request
      this.currentAction = 'confirm'
      this.confirmModalTitle = '确认售后'
      this.confirmModalMessage = `确定要确认售后申请 #${request.afterSalesId} 吗？`
      this.showConfirmModal = true
    },

    // 显示退货地址表单
    async openReturnForm(request) {
      this.currentRequest = request
      // 从后端获取对应订单的收货地址和联系人信息
      try {
        // 假设orderNumber是订单ID，从orderNumber中提取数字部分
        const orderId = request.orderNumber.replace('ORD', '')
        const response = await fetch(`/api/customer-orders/${orderId}`)
        if (response.ok) {
          const order = await response.json()
          // 填充订单的收货地址和联系人信息
          this.returnForm.address = order.address || ''
          this.returnForm.contact = order.name || ''
          this.returnForm.phone = order.phone || ''
        }
      } catch (err) {
        console.error('获取订单信息失败', err)
        // 失败时保持表单为空
        this.returnForm.address = ''
        this.returnForm.contact = ''
        this.returnForm.phone = ''
      }
      this.showReturnForm = true
    },

    // 关闭退货地址表单
    closeReturnForm() {
      this.showReturnForm = false
      this.currentRequest = null
      this.returnForm.address = ''
      this.returnForm.contact = ''
      this.returnForm.phone = ''
    },

    // 提交退货地址
    async submitReturnAddress() {
      if (!this.returnForm.address || !this.returnForm.contact || !this.returnForm.phone) {
        alert('请填写完整的退货地址信息')
        return
      }

      try {
        // 调用后端API更新售后状态并保存退货地址
        const response = await fetch(`/api/after-sales/${this.currentRequest.id}/status?status=待退货`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            returnAddress: this.returnForm.address,
            returnContact: this.returnForm.contact,
            returnPhone: this.returnForm.phone
          })
        })

        if (response.ok) {
          // 更新本地状态
          this.currentRequest.status = '待退货'
          this.currentRequest.returnAddress = this.returnForm.address
          this.currentRequest.returnContact = this.returnForm.contact
          this.currentRequest.returnPhone = this.returnForm.phone
          this.closeReturnForm()
          alert('退货地址已发送给客户！')
        } else {
          throw new Error('退货地址保存失败')
        }
      } catch (err) {
        console.error('退货地址保存失败', err)
        alert('退货地址保存失败，请稍后重试')
      }
    },

    // 确认收货
    async confirmReturnReceipt(request) {
      try {
        // 调用后端API更新售后状态为"待退款"
        const response = await fetch(`/api/after-sales/${request.id}/status?status=待退款`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            returnAddress: request.returnAddress,
            returnContact: request.returnContact,
            returnPhone: request.returnPhone,
            confirmReceipt: true
          })
        })

        if (response.ok) {
          // 更新本地状态
          request.status = '待退款'
          alert('确认收货成功，准备处理退款！')
          // 自动打开退款表单
          this.openRefundForm(request)
        } else {
          throw new Error('确认收货失败')
        }
      } catch (err) {
        console.error('确认收货失败', err)
        alert('确认收货失败，请稍后重试')
      }
    },

    // 拒绝售后
    rejectAfterSales(request) {
      this.currentRequest = request
      this.currentAction = 'reject'
      this.confirmModalTitle = '拒绝售后'
      this.confirmModalMessage = `确定要拒绝售后申请 #${request.afterSalesId} 吗？`
      this.showConfirmModal = true
    },

    // 关闭确认弹窗
    closeConfirmModal() {
      this.showConfirmModal = false
      this.currentRequest = null
      this.currentAction = ''
    },

    // 执行确认操作
    async confirmAction() {
      try {
        const status = this.currentAction === 'confirm' ? '已完成' : '已拒绝'
        // 调用后端API更新售后状态
        const response = await fetch(`/api/after-sales/${this.currentRequest.id}/status?status=${status}`, {
          method: 'PUT'
        })
        if (!response.ok) {
          throw new Error('更新售后状态失败')
        }
        // 重新获取售后申请列表
        await this.fetchAfterSalesRequests()
        this.showSuccessMessage(`售后申请 #${this.currentRequest.afterSalesId} 已${this.currentAction === 'confirm' ? '确认' : '拒绝'}`)
      } catch (err) {
        console.error(err)
        // 失败时直接更新本地状态
        if (this.currentAction === 'confirm') {
          this.currentRequest.status = '已完成'
        } else if (this.currentAction === 'reject') {
          this.currentRequest.status = '已拒绝'
        }
        this.showSuccessMessage(`售后申请 #${this.currentRequest.afterSalesId} 已${this.currentAction === 'confirm' ? '确认' : '拒绝'}`)
      } finally {
        this.closeConfirmModal()
      }
    },

    // 显示成功消息
    showSuccessMessage(message) {
      alert(message)
    }
  }
}
</script>

<style scoped>
/* 页面头部 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1.2rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.header-eyebrow {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.page-title {
  margin: 0;
  font-size: 2rem;
  color: #0f172a;
  letter-spacing: -0.03em;
}

/* 操作按钮 */
.action-buttons {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.action-buttons .btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.4rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 999px;
  border: none;
  background: rgba(15, 23, 42, 0.05);
  color: #0f172a;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-buttons .btn:hover {
  background: rgba(15, 23, 42, 0.08);
  transform: translateY(-1px);
}

.action-buttons .btn.active {
  background: linear-gradient(135deg, #111113, #2c2c2e);
  color: white;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
}

.action-buttons .btn-gray {
  background: rgba(148, 163, 184, 0.14);
  color: #64748b;
}

.action-buttons .btn-gray:hover {
  background: rgba(148, 163, 184, 0.2);
}

.btn-icon {
  font-size: 1.1rem;
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 700;
  background: #ef4444;
  color: white;
}

/* 统计栏 */
.summary-bar {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.2rem;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(18px);
}

.summary-icon {
  font-size: 1.4rem;
}

.summary-label {
  flex: 1;
  color: #64748b;
  font-size: 0.9rem;
}

.summary-value {
  font-size: 1.4rem;
  font-weight: 700;
}

.summary-value.pending {
  color: #f59e0b;
}

.summary-value.processing {
  color: #3b82f6;
}

.summary-value.completed {
  color: #10b981;
}

.summary-value.rejected {
  color: #ef4444;
}

.loading {
  text-align: center;
  padding: 3rem;
  color: #64748b;
}

/* 请求列表 */
.after-sales-list {
  margin-top: 1rem;
}

.request-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(500px, 1fr));
  gap: 1.25rem;
}

.request-card {
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.72);
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(18px);
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.request-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.1);
}

/* 状态时间线 */
.status-timeline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}

.timeline-step {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.step-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 0.9rem;
  background: rgba(15, 23, 42, 0.06);
  transition: all 0.2s ease;
}

.timeline-step.completed .step-icon {
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
}

.timeline-step.current .step-icon {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.timeline-step.pending .step-icon {
  background: rgba(15, 23, 42, 0.06);
  color: #9ca3af;
}

.step-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: #64748b;
}

.timeline-step.completed .step-label {
  color: #10b981;
}

.timeline-step.current .step-label {
  color: #3b82f6;
}

.step-line {
  flex: 1;
  height: 2px;
  margin: 0 0.5rem;
  background: rgba(15, 23, 42, 0.1);
}

.timeline-step.completed .step-line {
  background: rgba(16, 185, 129, 0.3);
}

.timeline-step.current .step-line {
  background: rgba(59, 130, 246, 0.3);
}

/* 请求头部 */
.request-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.2rem;
}

.request-id {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f172a;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
}

.status-pending {
  background: rgba(245, 158, 11, 0.12);
  color: #d97706;
}

.status-processing {
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
}

.status-waiting-return {
  background: rgba(168, 85, 247, 0.12);
  color: #9333ea;
}

.status-waiting-refund {
  background: rgba(236, 72, 153, 0.12);
  color: #db2777;
}

.status-completed {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}

.status-rejected {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
}

.status-unknown {
  background: rgba(15, 23, 42, 0.06);
  color: #64748b;
}

/* 请求信息 */
.request-info {
  margin-bottom: 1.25rem;
}

.info-row {
  display: grid;
  grid-template-columns: 24px 60px minmax(0, 1fr);
  gap: 0.5rem;
  align-items: center;
  padding: 0.5rem 0;
}

.info-icon {
  font-size: 0.95rem;
}

.info-label {
  color: #64748b;
  font-size: 0.88rem;
  font-weight: 500;
}

.info-value {
  color: #0f172a;
  font-size: 0.9rem;
}

.type-tag {
  display: inline-flex;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
}

.type-refund {
  background: rgba(236, 72, 153, 0.12);
  color: #db2777;
}

.type-exchange {
  background: rgba(168, 85, 247, 0.12);
  color: #9333ea;
}

.type-return {
  background: rgba(245, 158, 11, 0.12);
  color: #d97706;
}

.type-other {
  background: rgba(15, 23, 42, 0.06);
  color: #64748b;
}

/* 操作按钮 */
.actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding-top: 1rem;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 1.1rem;
  border-radius: 999px;
  border: none;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-info {
  background: rgba(23, 162, 184, 0.12);
  color: #0891b2;
}

.btn-info:hover {
  background: rgba(23, 162, 184, 0.18);
}

.btn-success {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}

.btn-success:hover {
  background: rgba(16, 185, 129, 0.18);
}

.btn-warning {
  background: rgba(245, 158, 11, 0.12);
  color: #d97706;
}

.btn-warning:hover {
  background: rgba(245, 158, 11, 0.18);
}

.btn-danger {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
}

.btn-danger:hover {
  background: rgba(239, 68, 68, 0.18);
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .summary-bar {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  
  .request-list {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .summary-bar {
    grid-template-columns: 1fr;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .action-buttons .btn {
    width: 100%;
    justify-content: center;
  }
  
  .actions {
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
  }
  
  .status-timeline {
    flex-wrap: wrap;
  }
  
  .timeline-step {
    margin-bottom: 0.5rem;
  }
  
  .step-line {
    display: none;
  }
  
  .info-row {
    grid-template-columns: 24px 1fr;
  }
  
  .info-label {
    display: none;
  }
}

/* 弹窗样式 */
.notification-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  overflow: hidden;
  pointer-events: auto;
  animation: overlayFadeIn 0.3s ease;
}

@keyframes overlayFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(255, 255, 255, 0.9);
  width: 90%;
  max-width: 520px;
  max-height: 85vh;
  border-radius: 28px;
  overflow: hidden;
  animation: modalFadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 
    0 25px 50px -12px rgba(15, 23, 42, 0.25),
    0 0 0 1px rgba(15, 23, 42, 0.08),
    0 4px 6px -1px rgba(15, 23, 42, 0.1);
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: auto;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: translate(-50%, -45%) scale(0.92);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

.modal-open {
  overflow: hidden;
  position: fixed;
  width: 100%;
}

.modal-header {
  padding: 1.25rem 1.5rem;
  border-bottom: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.03) 0%, rgba(15, 23, 42, 0.01) 100%);
}

.modal-header h3 {
  margin: 0;
  color: #0f172a;
  font-size: 1.15rem;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.close-btn {
  background: rgba(15, 23, 42, 0.06);
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #64748b;
  padding: 0.5rem;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(15, 23, 42, 0.1);
  color: #0f172a;
  transform: scale(1.05);
}

.close-btn:active {
  transform: scale(0.95);
}

.modal-body {
  padding: 1.5rem;
  line-height: 1.7;
  border-bottom: none;
  background: white;
}

.modal-body p {
  margin: 0.75rem 0;
  color: #475569;
  font-size: 0.95rem;
}

.modal-body p strong {
  color: #0f172a;
  font-weight: 600;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #0f172a;
  font-size: 0.9rem;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  font-size: 0.95rem;
  font-family: inherit;
  box-sizing: border-box;
  background: rgba(15, 23, 42, 0.02);
  color: #0f172a;
  transition: all 0.25s ease;
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: #94a3b8;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #3b82f6;
  background: white;
  box-shadow: 
    0 0 0 3px rgba(59, 130, 246, 0.1),
    0 1px 2px rgba(15, 23, 42, 0.05);
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
  line-height: 1.6;
}

.text-muted {
  color: #94a3b8;
  font-size: 0.825rem;
  margin-top: 0.5rem;
  display: block;
}

.modal-footer {
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.02) 0%, rgba(15, 23, 42, 0.04) 100%);
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}

.modal-footer .btn {
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.modal-footer .btn:first-child {
  background: rgba(15, 23, 42, 0.06);
  color: #64748b;
}

.modal-footer .btn:first-child:hover {
  background: rgba(15, 23, 42, 0.1);
  color: #0f172a;
  transform: translateY(-1px);
}

.modal-footer .btn:first-child:active {
  transform: translateY(0);
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 4px 14px rgba(59, 130, 246, 0.3);
}

.btn-primary:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
  transform: translateY(-2px);
}

.btn-primary:active {
  transform: translateY(0);
}

/* 响应式弹窗 */
@media (max-width: 768px) {
  .modal-content {
    width: 94%;
    margin: 0.75rem;
    border-radius: 24px;
  }
  
  .modal-header {
    padding: 1rem 1.25rem;
  }
  
  .modal-body {
    padding: 1.25rem;
  }
  
  .modal-footer {
    padding: 1rem 1.25rem;
    flex-direction: column;
    gap: 0.625rem;
  }
  
  .modal-footer .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>